import React, { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import {
  Grid,
  styled,
  TextField,
  Paper,
  Button,
  Typography,
  Autocomplete,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import PageHeader from '../../components/PageHeader';
import { Link, useNavigate, useParams } from 'react-router-dom';
import QrCode2Icon from '@mui/icons-material/QrCode2';

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import PersonDetail from '../../components/PersonDetail';
import { CardType } from '../../schema/card';
import { api } from '../../api';
import { Person, PersonStatus } from '../../schema/person';
import { PMTResponse, TrackingCardPMT, TrackingCardVHM, VHPlate } from '../../schema/external';
import { CircularProgress } from '@mui/material';

const ControlInDetail: React.FC = () => {
  const { person_id } = useParams();

  const navigate = useNavigate();

  const [reason, setReason] = useState<string>('');
  const [remark, setRemark] = useState<string>('');

  const [card_cv, setCard_cv] = useState<string>('');
  const [cardTypeID, setCardTypeID] = useState<number | null>(null);

  const [personStatus, setPersonStatus] = useState<PersonStatus | null>(null);

  const [personGPSDevice, setPersonGPSDevice] = useState<TrackingCardPMT | null>(null);
  const [vehicleGPSDevice, setVehicleGPSDevice] = useState<TrackingCardVHM | null>(null);
  const [vehiclePlateId, setVehiclePlateId] = useState<VHPlate | null>(null);
  const [PMTList, setPMTList] = useState<TrackingCardPMT[]>([]);
  const [VHMList, setVHMList] = useState<TrackingCardVHM[]>([]);

  const [vehiclePlateIdList, setVehiclePlateIdList] = useState<VHPlate[]>([]);
  const [vehiclePlateIdListBak, setVehiclePlateIdListBak] = useState<VHPlate[]>([]);

  const [cardTypeList, setCardTypeList] = useState<CardType[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [dialog_contect, setDialog_contect] = useState<string>('');
  const [errorMissingModal, setErrorMissingModal] = useState<string>('');
  const [countUp, setCountUp] = useState(0);
  const [contactIT, setContactIT] = useState(true);

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (isLoading) {
      return;
    }
    if (person_id) {
      if (vehicleGPSDevice && vehiclePlateId == null) {
        setErrorMissingModal('กรุณาใส่หมายเลขทะเบียนยานพาหนะ');
        setOpenConfirmDialog(true);
        return;
      }
      if (!personGPSDevice?.dev_eui) {
        setDialog_contect(
          'ไม่พบข้อมูลอุปกรณ์ติดตามประจำตัว หากมีการเข้าพื้นที่ควบคุม ควรจะมีการเพิ่มข้อมูลอุปกรณ์ติดตาม'
        );
        setOpenConfirmDialog(true);
        return;
      }
      send_data();
    }
  };

  const send_data = () => {
    setIsLoading(true);
    api
      .post(`/exchange-card`, {
        person_id: person_id,
        card_id: card_cv,
        card_type_id: cardTypeID,
        reason: reason,
        remark: remark,
        person_gps_id: personGPSDevice?.dev_eui || '',
        vehicle_gps_id: vehicleGPSDevice?.imei_id || '',
        vehicle_plate_name: vehiclePlateId?.name || '',
        vehicle_plate_id: vehiclePlateId?.uid || ''
      })
      .then(() => {
        navigate('/control-in-out');
      })
      .catch(function (error: AxiosError) {
        if (error.response) {
          if (typeof error.response.data === 'object') {
            setErrorMissingModal(JSON.stringify(error.response.data));
          } else {
            setErrorMissingModal(error.response.status + ': ' + error.response.statusText);
          }
          setOpenConfirmDialog(true);
        }
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (person_id) {
      api.get<Person>(`person/${person_id}`).then((res) => {
        const { data } = res;
        setPersonStatus(data);
      });
      api.get(`/exchange-card/person/${person_id}`).then((res) => {
        const { data } = res;
        if (data?.exchange_card_id) {
          navigate(`/control-in-out/out/${person_id}`);
        }
      });
    }
  }, [person_id]);

  useEffect(() => {
    api.get<PMTResponse<TrackingCardPMT[]>>(`bypass/pmt/online`).then((res) => {
      const { data } = res;
      const arr_pmt = [];
      if (data.data) {
        for (let i = 0; i < data.data.length; i++) {
          if (data.data[i].status == 'Online') {
            arr_pmt.push(data.data[i]);
          }
        }
        setPMTList(arr_pmt);
      }
    });

    api.get<TrackingCardVHM[]>(`bypass/vhm`).then((res) => {
      const { data } = res;
      const arr_vhm = [];
      if (data) {
        for (let i = 0; i < data.length; i++) {
          if (data[i].status == 'uninstall') {
            arr_vhm.push(data[i]);
          }
        }
        setVHMList(arr_vhm);
      }
    });

    api.get<VHPlate[]>(`bypass/vhm_plate`).then((res) => {
      const { data } = res;
      if (data) {
        setVehiclePlateIdList(data);
        setVehiclePlateIdListBak(data);
      }
    });
  }, []);

  useEffect(() => {
    if (personStatus) {
      api.get(`/person/${person_id}/ost/status`).then((res) => {
        const { data } = res;
        api
          .get(`/card_type`, {
            params: {
              has_wpm: personStatus?.is_control_area_active,
              has_ost: data.is_active || data.is_temp_active
            }
          })
          .then((res) => {
            const { data } = res;

            setCardTypeList(data);
          });
      });
    }
  }, [person_id, personStatus]);

  return (
    <Container>
      <PageHeader text="การควบคุมบุคคลเข้า" />

      <Button component={Link} to={`/control-in-out`} sx={{ my: 3, p: 0 }}>
        <ArrowBackIosIcon />
        การควบคุมบุคคลเข้า-ออก
      </Button>

      <PersonDetail person_id={person_id} />
      <Paper sx={{ p: 1, mt: 3 }}>
        <Typography variant="h6" sx={{ p: 2 }}>
          เพิ่มข้อมูลแลกบัตรเข้า
        </Typography>
        <Form onSubmit={handleFormSubmit} sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="หมายเลขบัตร"
                placeholder="หมายเลขบัตร"
                value={card_cv}
                variant="outlined"
                fullWidth
                onChange={(e) =>
                  setCard_cv(
                    e.target.value
                      .replaceAll('|', '')
                      .replaceAll('n', '0')
                      .replaceAll('m', '1')
                      .replaceAll('l', '2')
                      .replaceAll('k', '3')
                      .replaceAll('j', '4')
                      .replaceAll('i', '5')
                      .replaceAll('h', '6')
                      .replaceAll('g', '7')
                      .replaceAll('f', '8')
                      .replaceAll('e', '9')
                  )
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <QrCode2Icon />
                    </InputAdornment>
                  )
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    document.getElementById('personGPSDevice')?.focus();
                  }
                }}
                autoComplete="off"
                required
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="card_type">ประเภทบัตรที่แลก</InputLabel>
                <Select
                  labelId="card_type"
                  value={cardTypeID ? `${cardTypeID}` : ''}
                  label="ประเภทบัตรที่แลก"
                  placeholder="ประเภทบัตรที่แลก"
                  onChange={(e) => setCardTypeID(parseInt(e.target.value))}
                  required
                >
                  {cardTypeList.map((data, index) => (
                    <MenuItem key={index} value={`${data.id}`}>
                      {data.type_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                id="personGPSDevice"
                options={PMTList}
                getOptionLabel={(option) => (option?.dev_eui ? option.dev_eui : '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="เลขอุปกรณ์ติดตามประจำตัว"
                    placeholder="เลขอุปกรณ์ติดตามประจำตัว"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <QrCode2Icon />
                        </InputAdornment>
                      )
                    }}
                  />
                )}
                onChange={(_, value) => {
                  setPersonGPSDevice(value);
                  if (value) {
                    document.getElementById('vehicleGPSDevice')?.focus();
                  }
                }}
                value={personGPSDevice}
                isOptionEqualToValue={(option, v) => v?.id == option?.id}
                fullWidth
                autoHighlight={true}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    document.getElementById('vehicleGPSDevice')?.focus();
                  }
                }}
                onInputChange={(_, newInputValue) => {
                  const gps_id = String(newInputValue).split(':');
                  // if (newInputValue == '') {
                  //   setPersonGPSDevice(null);
                  // }
                  const data = PMTList.find(
                    (data) => data.dev_eui == (gps_id.length >= 3 ? gps_id[3] : newInputValue)
                  );
                  if (data) {
                    setPersonGPSDevice(data);
                  } else if (gps_id.length >= 3 && gps_id[3].length >= 16){
                    setContactIT(false);
                    setErrorMissingModal('หมายเลขอุปกรณ์นี้ไม่พร้อมใช้งาน');
                    setOpenConfirmDialog(true);
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                id="vehicleGPSDevice"
                options={VHMList}
                getOptionLabel={(option) => (option?.imei_id ? option.imei_id : '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="เลขอุปกรณ์ติดตามยานพาหนะ"
                    placeholder="เลขอุปกรณ์ติดตามยานพาหนะ"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <QrCode2Icon />
                        </InputAdornment>
                      )
                    }}
                  />
                )}
                onChange={(_, value) => {
                  setVehicleGPSDevice(value);
                  if (value) {
                    document.getElementById('vehicleRegistrationId')?.focus();
                  }
                }}
                value={vehicleGPSDevice}
                isOptionEqualToValue={(option, v) => v?._id == option?._id}
                fullWidth
                autoHighlight={true}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    document.getElementById('vehicleRegistrationId')?.focus();
                  }
                }}
                onInputChange={(_, newInputValue) => {
                  // if (newInputValue == '') {
                  //   setVehicleGPSDevice(null);
                  // }
                  const data = VHMList.find((data) => data.imei_id == newInputValue);
                  if (data) {
                    setVehicleGPSDevice(data);
                  } else if (newInputValue.length >= 16){
                    setContactIT(false);
                    setErrorMissingModal('หมายเลขอุปกรณ์นี้ไม่พร้อมใช้งาน');
                    setOpenConfirmDialog(true);
                  }
                }}
              />
            </Grid>
            {/* disable when not require wpm */}
            <Grid item xs={6}>
              <Autocomplete
                id="vehicleRegistrationId"
                options={vehiclePlateIdList}
                getOptionLabel={(option) => (option?.name ? option.name : '')}
                autoHighlight={true}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="หมายเลขทะเบียนยานพาหนะ"
                    placeholder="หมายเลขทะเบียนยานพาหนะ"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <QrCode2Icon />
                        </InputAdornment>
                      )
                    }}
                    required={vehicleGPSDevice != null}
                  />
                )}
                onChange={(_, value) => {
                  setVehiclePlateId(value);
                  if (value) {
                    document.getElementById('reason')?.focus();
                  }
                }}
                value={vehiclePlateId}
                isOptionEqualToValue={(option, v) => v?.uid == option?.uid}
                fullWidth
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    document.getElementById('reason')?.focus();
                  }
                }}
                onInputChange={(_, newInputValue) => {
                  // if (newInputValue == '') {
                  //   setVehicleGPSDevice(null);
                  // }
                  const tempPlate = vehiclePlateIdListBak;
                  setVehiclePlateIdList([])
                  if (newInputValue != "") {
                    const plate = tempPlate.filter((data) => data.name.includes(newInputValue));
                    console.log(plate)
                    setTimeout(() => {
                      setVehiclePlateIdList(plate)
                      setCountUp(countUp+1);
                    }, 500);
                  } else {
                    setVehiclePlateIdList(vehiclePlateIdListBak)
                  }
                  
                  const data = vehiclePlateIdList.find((data) => data.name == newInputValue);
                  if (data) {
                    setVehiclePlateId(data);
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                id="reason"
                label="วัตถุประสงค์"
                variant="outlined"
                placeholder="วัตถุประสงค์"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    document.getElementById('remark')?.focus();
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="remark"
                label="remark"
                variant="outlined"
                placeholder="remark"
                multiline
                minRows={4}
                value={remark}
                onChange={(event) => setRemark(event.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    document.getElementById('submit')?.focus();
                  }
                }}
              />
            </Grid>
          </Grid>
          <Button
            id="submit"
            type="submit"
            variant="contained"
            color="primary"
            sx={{ marginTop: 4 }}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress color="primary" size={'1.25rem'} sx={{ mr: 2 }} />
            ) : null}
            {`เพิ่มข้อมูล`}
          </Button>
        </Form>
      </Paper>
      <Dialog
        open={openConfirmDialog}
        onClose={() => {
          setOpenConfirmDialog(false);
          setErrorMissingModal('');
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle sx={{ px: 3 }} id="alert-dialog-title" variant="h5">
          {errorMissingModal == '' ? 'ยืนยันการแลกบัตร' : 'เกิดข้อผิดพลาด'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {errorMissingModal == '' ? dialog_contect : errorMissingModal}
            <br />
            <br />
            {(errorMissingModal != '' && contactIT) ? 'กรุณาบันทึกภาพหน้าจอนี้ประสานงานไปยังเจ้าหน้าที่' : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 2 }}>
          {errorMissingModal != '' ? (
            <Button
              onClick={() => {
                setOpenConfirmDialog(false);
                setErrorMissingModal('');
              }}
              color="primary"
              autoFocus
            >
              ยืนยัน
            </Button>
          ) : (
            <>
              <Button
                onClick={() => {
                  setOpenConfirmDialog(false);
                  setTimeout(() => {
                    document.getElementById('personGPSDevice')?.focus();
                  }, 300);
                }}
                color="primary"
                autoFocus
              >
                เพิ่มข้อมูลอุปกรณ์ติดตาม
              </Button>
              <Button
                onClick={() => {
                  setOpenConfirmDialog(false);
                  send_data();
                }}
                color="success"
                autoFocus
              >
                ไม่เพิ่มอุปกรณ์ติดตาม
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ControlInDetail;

const Container = styled('div')`
  padding: 16px;
  margin-bottom: 32px;
`;

const Form = styled('form')`
  display: flex;
  flex-direction: column;
  align-items: end;
`;
